import { useEffect, useMemo, useState } from "react";

import { useHistory } from "react-router-dom";

import { useGetBillingPlans } from "~/v2/api/hooks";
import type { ElitePerks } from "~/v2/api/rest/elite";
import { Path } from "~/v2/path";
import { getPrettyCash } from "~/v2/utils/formatting";
import { EliteMarketDataModalPres } from "./EliteMarketDataModal.pres";

const STORAGE_KEY = "elite_market_data_modal_dismissed";

interface EliteMarketDataModalConnectorProps {
  perks: ElitePerks | undefined;
  isPaper: boolean;
}

export const EliteMarketDataModalConnector = ({
  perks,
  isPaper,
}: EliteMarketDataModalConnectorProps) => {
  if (
    // live only
    isPaper ||
    // need to have perks data
    !perks ||
    // need to be 100k elite
    perks.elite_status !== "100k" ||
    // need to not be already enrolled in market data
    perks.market_data
  ) {
    return null;
  }

  return <EliteMarketDataModalConnectorInner />;
};

const EliteMarketDataModalConnectorInner = () => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);

  const { plans } = useGetBillingPlans();

  const marketDataPlanPrice: string | undefined = useMemo(() => {
    if (!plans?.monthly) {
      return undefined;
    }

    return getPrettyCash(plans.monthly.price / 100, {
      acceptZero: true,
    });
  }, [plans]);

  useEffect(() => {
    const shouldShowModal =
      marketDataPlanPrice &&
      window?.localStorage?.getItem(STORAGE_KEY) !== "true";

    if (shouldShowModal) {
      setIsOpen(true);
    }
  }, [marketDataPlanPrice]);

  const handleClose = () => {
    try {
      window?.localStorage?.setItem(STORAGE_KEY, "true");
    } catch (error) {
      console.error("Failed to dismiss elite market data modal", error);
    }
    setIsOpen(false);
  };

  const handleViewMarketData = () => {
    handleClose();
    history.push(Path.ROUTE_MARKET_DATA);
  };

  return (
    <EliteMarketDataModalPres
      isOpen={isOpen}
      onClose={handleClose}
      onViewMarketData={handleViewMarketData}
      marketDataPlanPrice={marketDataPlanPrice}
    />
  );
};
